/**
 * Floating combat text (#48) — reddish damage / greenish heal numbers that
 * float up above a unit and fade out over ~1s. #345 adds a THIRD kind:
 * "Stunned!"/"Rooted!"/... CC-status callouts, reusing this SAME rise/fade/
 * lifetime/pooling system (not a new above-head label mechanism). #463 adds a
 * FOURTH kind, the removal-side mirror: "-Stun"/"-Root"/... callouts when a
 * hard-CC aura ENDS (expiry / break-on-damage / dispel) — same system again.
 * #833 adds a FIFTH kind, `chat`: in-game player speech, which is the WHOLE
 * of chat v1's presentation (no window, no log, no scrollback) — same system
 * a fifth time, with per-kind lifetime/rise overrides so speech drifts up
 * SLOWER and lingers long enough to read.
 *
 * Two layers, deliberately split so the animation math is testable without
 * Pixi:
 *   1. PURE lifecycle (this half): `CombatTextStyle`, the `Floater` model,
 *      and `advanceFloater` (position/opacity at a given age). No DOM, no
 *      Pixi — unit-tested directly.
 *   2. Pixi manager (`combatTextManager.ts`): owns Text objects on the `fx`
 *      layer, pools them, caps concurrency, and drives them from the pure
 *      lifecycle each frame.
 *
 * CUSTOMIZATION IS A HARD REQUIREMENT. Every visual knob lives on
 * `CombatTextStyle`; nothing in the draw path is a magic number. Swap the
 * style object (future settings UI / per-user theme) and the treatment
 * changes with zero logic edits.
 *
 * Data source: the server ships per-tick `events` on the wire snapshot
 * (#48, PROTOCOL_VERSION 7; #345 added the `ccApplied` kind, #463 added
 * `ccRemoved`) — accurate, per-hit, attributable. This module consumes those
 * events; it does not derive numbers/labels from hp deltas or aura state.
 * #833's chat rides a SEPARATE per-snapshot array (`WireSimState.chat`), not
 * `events`, because a chat line is spoken BY a seat rather than inflicted ON
 * a target — see `WIRE_CHAT_MESSAGE_SCHEMA`.
 */

import type { CcClass, WireEvent } from "@pit/protocol";

/**
 * Everything a floater can be (#833).
 *
 * Was `WireEvent["kind"]`, which stopped being expressible the moment chat
 * arrived: chat is deliberately NOT a `WireEvent` (folding it in would feed
 * it to the impact-audio drain, so pressing Enter would play a damage
 * sound — see `WIRE_CHAT_MESSAGE_SCHEMA`). So the floater kind space is
 * declared HERE, explicitly, as the wire-event kinds PLUS the presentation-
 * only ones. Every per-kind resolver below switches exhaustively over it
 * with an `assertNever` default, so a sixth kind is a compile error until
 * it has a colour, a label, a lifetime and a rise distance.
 */
export type FloaterKind = WireEvent["kind"] | "chat";

/**
 * All visual treatment for floating combat text. One object, fully
 * data-driven. No draw-path literals live outside this.
 */
export interface CombatTextStyle {
  /** Damage number color (any CSS/Pixi color string). */
  readonly damageColor: string;
  /** Heal number color. */
  readonly healColor: string;
  /** CC-callout ("Stunned!"/...) color (#345). */
  readonly ccColor: string;
  /** CC-REMOVAL callout ("-Stun"/...) color (#463). Distinct from `ccColor`
   *  by default so onset and removal read as visually different events. */
  readonly ccRemovedColor: string;
  /** Chat-speech color (#833). Deliberately a NEUTRAL, non-combat colour so
   *  speech never reads as a damage/heal/CC number at a glance. NOT team-
   *  coloured in v1: the floater anchors above the speaker, which is what
   *  conveys identity, and a per-team palette would need viewer-team
   *  plumbing into a resolver that is (correctly) kind-only. */
  readonly chatColor: string;
  /** Total lifetime in ms before the floater is removed (~1000). Applies to
   *  every kind EXCEPT `chat` — see `chatLifetimeMs`. */
  readonly lifetimeMs: number;
  /**
   * Chat's own lifetime in ms (#833). MUCH longer than `lifetimeMs`: a
   * damage number is a glanceable 2-3 digits, a sentence is not — a message
   * that vanished in 1000 ms would be unreadable, and floating text is the
   * ONLY presentation chat has (there is no log to scroll back to). 3500 ms
   * is roughly the time to read a full `MAX_CHAT_TEXT_LEN` line at a
   * comfortable pace.
   *
   * This is ALSO half of "chat rises slower": rise is `easeOut(age/lifetime)
   * * riseDistance`, so a 3.5× longer lifetime alone makes the rise at any
   * given age dramatically shallower.
   */
  readonly chatLifetimeMs: number;
  /** How far the number rises over its life, in screen px. Applies to every
   *  kind EXCEPT `chat` — see `chatRiseDistancePx`. */
  readonly riseDistancePx: number;
  /**
   * Chat's own total rise distance in screen px (#833). SHORTER than
   * `riseDistancePx` (36 vs 42), which is the other half of "rises slower":
   * combined with the longer `chatLifetimeMs`, chat's rise at ANY age is
   * strictly below damage's at that same age. Shorter also keeps a
   * long-lived message from drifting off the top of its speaker.
   */
  readonly chatRiseDistancePx: number;
  /** Font size in px. */
  readonly fontSizePx: number;
  /** Font weight. */
  readonly fontWeight: number;
  /** Opacity ramp over life. */
  readonly fadeCurve: "linear" | "easeOut";
  /** Spawn height above the unit anchor, in screen px. */
  readonly offsetAbovePx: number;
  /** Hard cap on concurrent floaters (older ones evicted first). */
  readonly maxConcurrent: number;
}

/** Default treatment. Tweak freely — this is the single source of truth. */
export const DEFAULT_COMBAT_TEXT_STYLE: CombatTextStyle = {
  damageColor: "#ff5040",
  healColor: "#54d06a",
  ccColor: "#e8c840",
  ccRemovedColor: "#9fb8d1",
  chatColor: "#f2f4f7",
  lifetimeMs: 1000,
  chatLifetimeMs: 3500,
  riseDistancePx: 42,
  chatRiseDistancePx: 36,
  fontSizePx: 18,
  fontWeight: 700,
  fadeCurve: "easeOut",
  offsetAbovePx: 28,
  maxConcurrent: 64,
};

/**
 * A live floating number/label. World-anchored X/Y; screen rise is applied
 * on top. `amount` backs "damage"/"heal" (hp delta); `ccClass` backs
 * "ccApplied" (#345, the hard-CC onset callout) / "ccRemoved" (#463, the
 * removal-side mirror); `chatText` backs "chat" (#833) — exactly one of the
 * three is meaningful per `kind`, mirroring the wire shapes they come from.
 */
export interface Floater {
  /** Monotonic id (spawn order) — also used for deterministic eviction. */
  readonly id: number;
  readonly kind: FloaterKind;
  /** hp delta (damage/heal); `0` (unused) for a "ccApplied"/"ccRemoved"/
   *  "chat" floater. */
  readonly amount: number;
  /** CC classification (#345/#463); `null` for a "damage"/"heal"/"chat"
   *  floater. */
  readonly ccClass: CcClass | null;
  /** Chat message text (#833); `null` for every other kind. */
  readonly chatText: string | null;
  /** World anchor (unit position at spawn). Camera projects it each frame. */
  readonly worldX: number;
  readonly worldY: number;
  /** Wall-clock ms when spawned. */
  readonly spawnedMs: number;
}

/** Per-frame presentation of a floater: screen offset + opacity. */
export interface FloaterFrame {
  /** Additional upward screen offset (px, positive = up) beyond the anchor. */
  readonly risePx: number;
  /** Opacity 0..1. */
  readonly opacity: number;
  /** True once the floater has outlived its lifetime and should be removed. */
  readonly expired: boolean;
}

function clamp01(x: number): number {
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/** easeOut (quadratic) — fast start, slow finish. */
function easeOut(t: number): number {
  const c = clamp01(t);
  return 1 - (1 - c) * (1 - c);
}

/**
 * How long a floater of `kind` lives, per style (#833). Every kind but
 * `chat` shares `style.lifetimeMs` — the four combat kinds are explicitly
 * unchanged by #833.
 */
export function floaterLifetimeMs(kind: FloaterKind, style: CombatTextStyle): number {
  switch (kind) {
    case "chat":
      return style.chatLifetimeMs;
    case "damage":
    case "heal":
    case "ccApplied":
    case "ccRemoved":
      return style.lifetimeMs;
    default:
      return assertNeverFloaterKind(kind);
  }
}

/**
 * How far a floater of `kind` rises over its whole life, per style (#833).
 * Same shared-except-chat rule as `floaterLifetimeMs`.
 */
export function floaterRiseDistancePx(kind: FloaterKind, style: CombatTextStyle): number {
  switch (kind) {
    case "chat":
      return style.chatRiseDistancePx;
    case "damage":
    case "heal":
    case "ccApplied":
    case "ccRemoved":
      return style.riseDistancePx;
    default:
      return assertNeverFloaterKind(kind);
  }
}

/**
 * Compute a floater's screen offset + opacity at `nowMs`. Pure. The rise
 * eases out (fast then settling); opacity ramps down per `fadeCurve`.
 *
 * #833: lifetime and rise distance are resolved PER KIND (see the two
 * resolvers above) rather than read straight off the style, which is the
 * whole mechanism behind "speech floats up slower than a damage number" —
 * no separate animation path, no new above-head label system, just different
 * numbers through the same easing.
 */
export function advanceFloater(
  f: Floater,
  nowMs: number,
  style: CombatTextStyle,
): FloaterFrame {
  const age = nowMs - f.spawnedMs;
  const life = floaterLifetimeMs(f.kind, style);
  const riseDistance = floaterRiseDistancePx(f.kind, style);
  if (age >= life) {
    return { risePx: riseDistance, opacity: 0, expired: true };
  }
  const t = clamp01(age / life);
  const risePx = easeOut(t) * riseDistance;
  const opacity =
    style.fadeCurve === "easeOut" ? 1 - easeOut(t) : 1 - t;
  return { risePx, opacity: clamp01(opacity), expired: false };
}

/** Color for a floater kind, per style. Exhaustive — a new kind without a
 *  colour is a compile error, not a silently-black floater. */
export function floaterColor(kind: FloaterKind, style: CombatTextStyle): string {
  switch (kind) {
    case "damage":
      return style.damageColor;
    case "heal":
      return style.healColor;
    case "ccApplied":
      return style.ccColor;
    case "ccRemoved":
      return style.ccRemovedColor;
    case "chat":
      return style.chatColor;
    default:
      return assertNeverFloaterKind(kind);
  }
}

/**
 * The exclamation-point callout for a newly-applied hard-CC class (#345).
 * Exhaustive over `CcClass` (`assertNever` below) — a class added to the
 * protocol enum without a label here is a compile error, not a silent
 * blank floater. `slow` is included for exhaustiveness only; the server
 * never emits a `"slow"` `ccApplied` event (see `hardCcClasses` in
 * `@pit/sim`), so it never actually renders.
 */
function ccClassLabel(ccClass: CcClass): string {
  switch (ccClass) {
    case "stun":
      return "Stunned!";
    case "root":
      return "Rooted!";
    case "fear":
      return "Feared!";
    case "silence":
      return "Silenced!";
    case "mez":
      return "Mezzed!";
    case "disarm":
      return "Disarmed!";
    case "slow":
      return "Slowed!";
    default:
      return assertNeverCcClass(ccClass);
  }
}

/**
 * The dash-prefixed, short-form callout for a hard-CC class ENDING (#463) —
 * "-Stun"/"-Root"/... — deliberately distinct in style from `ccClassLabel`'s
 * exclamation-point onset callout so the two read as different events at a
 * glance. Exhaustive over `CcClass` (`assertNever` below), same discipline as
 * `ccClassLabel`. `slow` is included for exhaustiveness only; the server
 * never emits a `"slow"` `ccRemoved` event either (see `hardCcClasses` in
 * `@pit/sim`), so it never actually renders.
 */
function ccRemovedClassLabel(ccClass: CcClass): string {
  switch (ccClass) {
    case "stun":
      return "-Stun";
    case "root":
      return "-Root";
    case "fear":
      return "-Fear";
    case "silence":
      return "-Silence";
    case "mez":
      return "-Mez";
    case "disarm":
      return "-Disarm";
    case "slow":
      return "-Slow";
    default:
      return assertNeverCcClass(ccClass);
  }
}

function assertNeverCcClass(x: never): never {
  throw new Error(`unhandled CcClass: ${String(x)}`);
}

function assertNeverFloaterKind(x: never): never {
  throw new Error(`unhandled FloaterKind: ${String(x)}`);
}

/** Display text for a floater: heal prefixed with "+", damage bare, a CC
 *  onset callout its exclamation-point class label (#345), a CC removal
 *  callout its dash-prefixed short-form class label (#463), a chat floater
 *  the message VERBATIM (#833 — no name prefix: the anchor above the
 *  speaker's head is the attribution, and there is no player-name field on
 *  the wire to prefix with). Exhaustive over `FloaterKind`. */
export function floaterText(f: Floater): string {
  switch (f.kind) {
    case "damage":
      return String(f.amount);
    case "heal":
      return "+" + String(f.amount);
    case "ccApplied":
      return f.ccClass === null ? "" : ccClassLabel(f.ccClass);
    case "ccRemoved":
      return f.ccClass === null ? "" : ccRemovedClassLabel(f.ccClass);
    case "chat":
      return f.chatText ?? "";
    default:
      return assertNeverFloaterKind(f.kind);
  }
}
